import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Package,
  PlusCircle,
  FolderTree,
  History,
  Settings,
  Diamond,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  Receipt,
  Users,
  ClipboardList,
} from "lucide-react";

const OWNER_NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, testid: "sidebar-nav-home", end: true },
  { to: "/satis", label: "Yeni Satış", icon: ShoppingCart, testid: "sidebar-nav-pos" },
  { to: "/satislar", label: "Satışlar", icon: Receipt, testid: "sidebar-nav-sales" },
  { to: "/vardiyalar", label: "Vardiyalar", icon: ClipboardList, testid: "sidebar-nav-shifts" },
  { to: "/urunler", label: "Ürünler", icon: Package, testid: "sidebar-nav-products" },
  { to: "/yeni-urun", label: "Yeni Ürün", icon: PlusCircle, testid: "sidebar-nav-add-product" },
  { to: "/kategoriler", label: "Kategoriler", icon: FolderTree, testid: "sidebar-nav-categories" },
  { to: "/stok-hareketleri", label: "Stok Hareketleri", icon: History, testid: "sidebar-nav-movements" },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, testid: "sidebar-nav-users" },
  { to: "/audit", label: "İşlem Kayıtları", icon: ClipboardList, testid: "sidebar-nav-audit" },
  { to: "/kilavuz", label: "Kullanım Kılavuzu", icon: ClipboardList, testid: "sidebar-nav-guide" },
  { to: "/sistem-merkezi", label: "Sistem Merkezi", icon: ClipboardList, testid: "sidebar-nav-system" },
  { to: "/guvenlik", label: "Güvenlik", icon: ClipboardList, testid: "sidebar-nav-security" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testid: "sidebar-nav-settings" },
];

const MANAGER_NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, testid: "sidebar-nav-home", end: true },
  { to: "/satis", label: "Yeni Satış", icon: ShoppingCart, testid: "sidebar-nav-pos" },
  { to: "/satislar", label: "Satışlar", icon: Receipt, testid: "sidebar-nav-sales" },
  { to: "/vardiyalar", label: "Vardiyalar", icon: ClipboardList, testid: "sidebar-nav-shifts" },
  { to: "/urunler", label: "Ürünler", icon: Package, testid: "sidebar-nav-products" },
  { to: "/yeni-urun", label: "Yeni Ürün", icon: PlusCircle, testid: "sidebar-nav-add-product" },
  { to: "/kategoriler", label: "Kategoriler", icon: FolderTree, testid: "sidebar-nav-categories" },
  { to: "/stok-hareketleri", label: "Stok Hareketleri", icon: History, testid: "sidebar-nav-movements" },
  { to: "/kullanicilar", label: "Kasiyerler", icon: Users, testid: "sidebar-nav-users" },
];

const CASHIER_NAV = [
  { to: "/satis", label: "Yeni Satış", icon: ShoppingCart, testid: "sidebar-nav-pos", end: true },
  { to: "/satislar", label: "Satışlarım", icon: Receipt, testid: "sidebar-nav-sales" },
  { to: "/vardiyalar", label: "Vardiyalarım", icon: ClipboardList, testid: "sidebar-nav-shifts" },
];

const ROLE_LABEL = { owner: "SAHİP", manager: "YÖNETİM", cashier: "KASİYER" };

export default function Layout() {
  const { user, logout, isOwner, isManager, isCashier } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const NAV = isOwner ? OWNER_NAV : isManager ? MANAGER_NAV : CASHIER_NAV;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      <div className="px-6 py-6 border-b border-amber-900/20">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md flex items-center justify-center gold-gradient">
            <Diamond className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-serif-display text-xl leading-none text-amber-100">
              RIVACO
            </div>
            <div className="font-mono-code text-[10px] tracking-[0.25em] text-amber-200/60 mt-1">
              {ROLE_LABEL[user?.role] || "PORTAL"}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-amber-500/15 text-amber-100 border border-amber-500/30"
                  : "text-stone-300 hover:bg-stone-800/60 hover:text-amber-100 border border-transparent"
              }`
            }
          >
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-amber-900/20">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-200 text-xs font-semibold">
            {(user?.name || user?.username || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="text-xs min-w-0">
            <div className="text-amber-100 truncate">{user?.name}</div>
            <div className="text-stone-400 truncate">
              {user?.email || user?.username}
            </div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-stone-300 hover:bg-red-500/10 hover:text-red-300 transition border border-transparent hover:border-red-500/30"
        >
          <LogOut className="w-4 h-4" />
          Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#faf7f2]">
      <aside className="hidden lg:flex flex-col w-64 bg-stone-950 text-stone-100 border-r border-amber-900/20 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-stone-950 text-stone-100 flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[#faf7f2]/85 backdrop-blur border-b border-stone-200">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2 rounded-md border border-stone-200"
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div>
                <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
                  RIVACO — {ROLE_LABEL[user?.role] || "PORTAL"}
                </div>
                <div className="font-serif-display text-xl text-stone-900 leading-tight">
                  {isCashier ? "Satış Ekranı" : "Butik Yönetim Portalı"}
                </div>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-stone-500 font-mono-code">
              {new Date().toLocaleDateString("tr-TR", {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
                timeZone: "Europe/Istanbul",
              })}
            </div>
          </div>
          <div className="gold-line" />
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
